import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const LanguageContext = createContext(null);

const STORAGE_KEY = "ib_lang";

const dict = {
  es: {
    nav: {
      about: "Sobre mí",
      projects: "Proyectos",
      vision: "Visión",
      insights: "Notas",
      contact: "Contacto",
    },
    hero: {
      eyebrow: "Empresario · Girona",
      name: "Ivan Bautista",
      subtitle: "Empresario y creador de proyectos.",
      body:
        "Desarrollo iniciativas en deporte, energía y comunidad con el objetivo de construir organizaciones sólidas, sostenibles y con impacto real.",
      ctaProjects: "Ver proyectos",
      ctaContact: "Contacto",
      meta: "Construyendo desde",
      year: "2021",
    },
    about: {
      index: "01 — Sobre mí",
      title: "Sobre mí",
      p1:
        "Soy Ivan Bautista, emprendedor enfocado en la creación y desarrollo de proyectos con visión de largo plazo.",
      p2:
        "Mi trabajo se centra en construir organizaciones, comunidades y modelos de negocio capaces de generar valor de forma sostenible.",
      p3:
        "Actualmente desarrollo iniciativas en el ámbito del deporte amateur, la energía solar y la organización de competiciones deportivas.",
      pillars: [
        { k: "Deporte amateur", v: "Comunidad y competición" },
        { k: "Energía solar", v: "Patrimonio sostenible" },
        { k: "Organización", v: "Estructuras de largo plazo" },
      ],
    },
    projects: {
      index: "02 — Proyectos",
      title: "Proyectos",
      kicker:
        "Iniciativas en activo. Cada una pensada como una organización con vocación de permanencia.",
      view: "Ver proyecto",
      visit: "Visitar",
      items: {
        proxima: {
          tag: "Deporte · Competición",
          name: "Proxima League",
          summary:
            "Competiciones de fútbol amateur diseñadas para ofrecer una experiencia organizada, competitiva y accesible para equipos y jugadores.",
        },
        solibrum: {
          tag: "Energía · Transición",
          name: "Solibrum",
          summary:
            "Proyecto orientado a facilitar el acceso a soluciones de energía solar mediante información, análisis y acompañamiento durante el proceso de adopción.",
        },
        athletic: {
          tag: "Comunidad · Girona",
          name: "Athletic Gironí",
          summary:
            "Club y comunidad deportiva enfocada en fomentar la práctica del fútbol y el desarrollo del deporte amateur en Girona.",
        },
      },
    },
    vision: {
      index: "03 — Visión",
      title: "Visión",
      pull:
        "Creo en la construcción de proyectos sólidos que aporten valor a las personas y contribuyan al desarrollo de sus comunidades.",
      body:
        "Mi objetivo es impulsar iniciativas capaces de crecer de forma sostenible, generar oportunidades y dejar un impacto positivo a largo plazo.",
    },
    insights: {
      index: "04 — Notas",
      title: "Notas",
      kicker:
        "Reflexiones puntuales sobre liderazgo, organizaciones y construcción de proyectos.",
      read: "Leer",
      empty: "Próximamente nuevas publicaciones.",
      back: "Volver a Notas",
    },
    contact: {
      index: "05 — Contacto",
      title: "Contacto",
      kicker:
        "Para colaboraciones, propuestas o conversaciones. Respuesta personal.",
      name: "Nombre",
      email: "Correo electrónico",
      message: "Mensaje",
      send: "Enviar mensaje",
      sending: "Enviando…",
      success: "Mensaje recibido. Gracias por escribir.",
      error: "No se ha podido enviar. Inténtalo de nuevo en unos minutos.",
      networks: "Redes",
      personal: "Personal",
      projectsLabel: "Proyectos",
    },
    footer: {
      tagline: "Construyendo proyectos con visión de largo plazo.",
      rights: "Todos los derechos reservados.",
    },
  },
  en: {
    nav: {
      about: "About",
      projects: "Ventures",
      vision: "Vision",
      insights: "Notes",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Entrepreneur · Girona",
      name: "Ivan Bautista",
      subtitle: "Entrepreneur and project builder.",
      body:
        "I develop initiatives in sport, energy and community, focused on building solid, sustainable organizations with real impact.",
      ctaProjects: "Ventures",
      ctaContact: "Contact",
      meta: "Building since",
      year: "2021",
    },
    about: {
      index: "01 — About",
      title: "About",
      p1:
        "I am Ivan Bautista, an entrepreneur focused on creating and developing projects with a long-term vision.",
      p2:
        "My work is dedicated to building organizations, communities and business models that can generate value sustainably.",
      p3:
        "I currently lead initiatives across amateur sport, solar energy and the organization of sports competitions.",
      pillars: [
        { k: "Amateur sport", v: "Community & competition" },
        { k: "Solar energy", v: "Sustainable heritage" },
        { k: "Organization", v: "Long-term structures" },
      ],
    },
    projects: {
      index: "02 — Ventures",
      title: "Ventures",
      kicker:
        "Active initiatives. Each one designed as an organization built to last.",
      view: "View venture",
      visit: "Visit",
      items: {
        proxima: {
          tag: "Sport · Competition",
          name: "Proxima League",
          summary:
            "Amateur football competitions designed to offer an organized, competitive and accessible experience for teams and players.",
        },
        solibrum: {
          tag: "Energy · Transition",
          name: "Solibrum",
          summary:
            "A project focused on enabling access to solar energy solutions through information, analysis and guidance throughout the adoption process.",
        },
        athletic: {
          tag: "Community · Girona",
          name: "Athletic Gironí",
          summary:
            "A football club and sports community dedicated to promoting amateur football and grassroots sport in Girona.",
        },
      },
    },
    vision: {
      index: "03 — Vision",
      title: "Vision",
      pull:
        "I believe in building solid projects that bring value to people and contribute to the development of their communities.",
      body:
        "My aim is to lead initiatives capable of growing sustainably, creating opportunities and leaving a positive long-term impact.",
    },
    insights: {
      index: "04 — Notes",
      title: "Notes",
      kicker:
        "Short essays on leadership, organizations and the craft of building projects.",
      read: "Read",
      empty: "New notes are on the way.",
      back: "Back to Notes",
    },
    contact: {
      index: "05 — Contact",
      title: "Contact",
      kicker:
        "For collaborations, proposals or conversations. Personal reply.",
      name: "Name",
      email: "Email",
      message: "Message",
      send: "Send message",
      sending: "Sending…",
      success: "Message received. Thank you for writing.",
      error: "Could not send. Please try again in a few minutes.",
      networks: "Networks",
      personal: "Personal",
      projectsLabel: "Ventures",
    },
    footer: {
      tagline: "Building projects with a long-term vision.",
      rights: "All rights reserved.",
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "es";
    return window.localStorage.getItem(STORAGE_KEY) || "es";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: dict[lang] || dict.es,
    }),
    [lang]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
